import { useEffect, useMemo, useState } from 'react';

import {
  sendGlobalLayoutProps,
  sendGlobalErrorProps,
  waitForElement,
  type MasterLayoutProps,
} from 'cu-master-layout-prop-manager';
import { hasAuthParams, useAuth as useOidcAuth } from 'react-oidc-context';

import { appConfig } from '@/shared/config';
import type { Nullable } from '@/shared/lib';

type OidcInnerError = {
  error?: string;
  error_description?: Nullable<string>;
  error_uri?: Nullable<string>;
  session_state?: string;
};

export function useAuth() {
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const auth = useOidcAuth();
  const token = auth.user?.access_token;
  const fullName = auth.user?.profile.name;
  // The cu-master-layout-2 contract (prop manager 1.0.9): token, environment, user name and this app's menu link.
  const globalProps = useMemo<MasterLayoutProps>(
    () => ({ token: token ?? '', env: appConfig.shell.env, fullName, activeLink: appConfig.shell.activeLink }),
    [token, fullName],
  );

  useEffect(() => {
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      void auth.signinRedirect();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasTriedSignin(true);
      sendGlobalLayoutProps(globalProps);
    }
  }, [auth, hasTriedSignin, globalProps]);

  useEffect(() => {
    if (!hasAuthParams() && auth.isAuthenticated && auth.user?.expired) {
      auth.signinSilent().catch(() => {
        void auth.signinRedirect();
      });
    }
  }, [auth, auth.user?.expired]);

  // Auth errors: report to the shell and sign in again when the provider asks for it.
  useEffect(() => {
    if (auth.isLoading || !auth.error) return;

    console.error('Auth error. Please check the defined auth credentials', auth.error);
    sendGlobalErrorProps({ errorType: 'generalError' });

    if (
      auth.error.message === 'login_required' ||
      (auth.error.innerError && (auth.error.innerError as OidcInnerError).error === 'login_required')
    ) {
      void auth.signinRedirect();
    }
  }, [auth, auth.error, auth.isLoading]);

  // Signed in: push the token to the shell and wire the shell logout button to the OIDC sign-out, once per token.
  useEffect(() => {
    if (!auth.isAuthenticated || !token) return;

    sendGlobalLayoutProps(globalProps);

    let logoutButton: Element | undefined;
    const onLogout = () => {
      void auth.signoutRedirect({ id_token_hint: auth.user?.id_token });
    };

    waitForElement('#master_layout_logout_btn')
      .then((element) => {
        logoutButton = element;
        element.removeAttribute('href');
        element.addEventListener('click', onLogout);
      })
      .catch(console.error);

    return () => {
      logoutButton?.removeEventListener('click', onLogout);
    };
  }, [auth, auth.isAuthenticated, token, globalProps]);

  return auth;
}
